(function() {

/**
 * Conversions and labeling of all units.
 * @module base
 * @namespace rwgps
 */
module.exports = {
    /**
     * Generic conversions that do not mess with precision of results. 
     * @class Convert
     */
    Convert: {
      /** Convert from miles or km to meters
       * @method longToM
       * @static
       * @param {float} value Miles or KM to convert to meters.
       * @return {float} Result in meters
       */
      longToM: function(value) { 
        if(rwgps.LabelMaker.metric) 
          return value * 1000;
        else
          return value * 1609.344;
      },
      /** Convert from meters to km or miles
       * @method mToLong
       * @static
       * @param {float} value Meters
       * @return {float} Result in KM or miles (if using metric or not)
       */
      mToLong: function(value) { 
        if(rwgps.LabelMaker.metric) 
          return value * 0.001;
        else
          return value * 0.000621371192;
      },
      /** Convert from miles or km to kilometers
       * @method longToKm
       * @static
       * @param {float} value Miles or KM to convert to KM
       * @return {float} Result in KM
       */
      longToKm: function(value) { 
        if(rwgps.LabelMaker.metric) 
          return value;
        else
          return value * 1.609344;
      },
      /** Convert from kilometers to km or miles
       * @method kmToLong
       * @static
       * @param {float} value KM
       * @return {float} Result in KM or miles (if using metric or not)
       */
      kmToLong: function(value) { 
        if(rwgps.LabelMaker.metric) 
          return value;
        else
          return value * 0.621371192;
      },
      /** Convert from feet/meters to meters
       * @method shortToM
       * @static
       * @param {float} value Feet or meters
       * @return {float} Result in meters
       */
      shortToM: function(value) {
        if(rwgps.LabelMaker.metric)
          return value;
        else
          return value * 0.3048006096012;
      },
      /** Convert from meters to feet or meters 
       * @method mToShort
       * @static
       * @param {float} value Meters to convert
       * @return {float} Result in feet or meters (if using metric or not)
       */
      mToShort: function(value) {
        if(rwgps.LabelMaker.metric)
          return value;
        else
          return value * 3.2808399;
      },
      /** Celsius to celsius or fahrenheit
       * @method cToDeg
       * @static
       * @param {float} value Degrees in C
       * @return {float} Result in F or C (if using metric units or not)
       */
      cToDeg: function(value) {
        if(rwgps.LabelMaker.metric)
          return value;
        else
          return value * 1.8 + 32;
      },
      /** Celsius or f to celsius
       * @method degToC
       * @static
       * @param {float} value Degrees in F or C (if using metric units or not)
       * @return {float} Result in C
       */
      degToC: function(value) {
        if(rwgps.LabelMaker.metric)
          return value;
        else
          return (value - 32) * 5/9;
      }
    },

    /**
     * Expects metric values, performs conversions, truncates
     * accordingly and labels optionally.
     * @class LabelMaker
     */
    LabelMaker: {
      /**
       * Whether we are using metric units or not. Very important property!
       * @property metric
       */
      metric: true,
      longUnit: function() {
        return this.metric ? "km" : "miles";
      },
      shortUnitAbbrev: function() {
        return this.metric ? 'm' : 'ft';
      },
      /** Converts KPH to MPH/KPH with 1 number after decimal point, and optionally labels.
       * @method speedLabel
       * @static
       * @param {float} value KPH value to format
       * @param {Boolean} label (optional) Whether or not to append the label kph/mph, defaults to false
       * @return {String} Converted/formatted/labeled result
       */
      speedLabel: function(value, label) {
        var newVal = this.metric ? value * 1 : value * 0.621371192;
        return newVal.toFixed(1) + (label ? (this.metric ? " kph" : " mph") : '');
      },
      /** Converts meters to KM/miles with specified precision, and optionally labels.
       * @method longLabel
       * @static
       * @param {float} value Value to format in meters
       * @param {String} label (optional) falsey gets no label, "full" gets km/miles, anything else gets km/mi
       * @param {int} precision (optional) Defaults to 1, value passed to toFixed before result is returned
       * @return {String} Converted/formatted/labeled result
       */
      longLabel: function(value, label, precision) {
        if(!precision) precision = 1;
        var newVal = this.metric ? value * .001 : value * 0.000621371192;
        return newVal.toFixed(precision) + (label ? (label == 'full' ? (this.metric ? ' km' : ' miles') : (this.metric ? " km" : " mi")) : '');
      },
      /** Converts meters to m/ft, rounds, optionally labels.
       * @method shortLabel
       * @static
       * @param {float} value Value to format in meters
       * @param {String} label (optional) falsey gets no label, "full" gets meters/feet, anything else gets m/ft
       * @return {String} Converted/formatted/labeled result
       */
      shortLabel: function(value, label) {
        var newVal = this.metric ? value : value * 3.2808399;
        return Math.round(newVal) + (label ? (label == 'full' ? (this.metric ? ' meters' : ' feet') : (this.metric ? " m" : " ft")) : '');
      },
      /** Converts seconds to formatted duration. Short format examples: 1h10m, 5m, 35s.  
       * Long format: HH:MM:SS.
       * @method durationLabel
       * @static
       * @param {int} seconds Duration in seconds to format
       * @param {Boolean} shortFormat (optional) Whether or not to use the short format.
       * @return {String} Converted/formatted/labeled result
       */
      durationLabel: function(seconds, shortFormat) {
        var hours = Math.floor(seconds / 3600);
        seconds -= hours * 3600;
        var minutes = Math.floor(seconds / 60);
        seconds = Math.floor(seconds - minutes * 60);
        if(shortFormat === 1) {
          return (hours > 0 ? (hours<10 ? '0' : '') + hours + ':' + (minutes<10 ? '0' : '') : '') + minutes + ':' + (seconds<10 ? '0' : '') + seconds;
        } else if(shortFormat) {
          var days = 0;
          if(hours > 99) {
            days = Math.floor(hours / 24);
            hours -= days * 24;
          }
          // 1h10m 5m 35s
          if(days > 0)
            return days + 'd' + (hours == 0 ? '' : hours + 'h')
          else if(hours > 0)
            return hours + 'h' + (minutes == 0 ? '' : minutes + 'm')
          else if(minutes > 9)
            return minutes + 'm'
          else if(minutes > 0)
            return minutes + 'm' + seconds + 's'
          else
            return seconds + 's'
        } else {
          return (hours<10 ? '0' : '') + hours + ':' + (minutes<10 ? '0' : '') + minutes + ':' + (seconds<10 ? '0' : '') + seconds;
        }
      },
      /** Converts a seconds/km pace appropriately (KM -> miles if not using metric) and returns a duration label
       * @method paceLabel
       * @static 
       * @param {int} secondsPerKm Pace in seconds/km
       * @param {Boolean} shortFormat (optional) Whether or not to use the short format.
       * @return {String} Converted/formatted/labeled result
       */
      paceLabel: function(secondsPerKm, shortFormat) {
        var paceConverted = Math.round(1/rwgps.Convert.kmToLong(1/secondsPerKm));
        return rwgps.LabelMaker.durationLabel(paceConverted, shortFormat);
      }
    }
  };
})();



// WEBPACK FOOTER //
// ./public/javascripts/rwgps/units.js